import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFamilyStore } from '@/store/familyStore';
import { MemberSearch } from '@/components/shared/search/MemberSearch';
import { getAncestorLineage } from '@/utils/family/ancestors';

interface AddChildFormData {
  requesterName: string;
  requesterPhone: string;
  childName: string;
  parentId: string;
}

interface AddChildFormProps {
  formData: AddChildFormData;
  onChange: (data: AddChildFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  initialParentId?: string;
}

export function AddChildForm({
  formData,
  onChange,
  onSubmit,
  initialParentId,
}: AddChildFormProps) {
  const { members } = useFamilyStore();
  const selectedParent = members.find(m => m.id === (initialParentId || formData.parentId));

  const handleChange = (field: keyof AddChildFormData, value: string) => {
    onChange({ ...formData, [field]: value });
  };

  const handleParentSelect = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      onChange({
        ...formData,
        parentId: member.id,
        requesterName: member.name
      });
    }
  };

  const parentLineage = selectedParent ? getAncestorLineage(selectedParent.id, members) : null;

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* Requester Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">بيانات مقدم الطلب</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>الاسم الرباعي</Label>
            <MemberSearch
              onSelect={handleParentSelect}
              placeholder="ابحث عن الأب..."
              className="w-full"
            />
            {parentLineage && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">{parentLineage}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="requesterPhone">رقم الجوال</Label>
            <Input
              id="requesterPhone"
              type="tel"
              dir="ltr"
              className="text-left"
              value={formData.requesterPhone}
              onChange={(e) => handleChange('requesterPhone', e.target.value)}
              placeholder="05xxxxxxxx"
              required
            />
          </div>
        </div>
      </div>

      {/* Child Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">بيانات المولود</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="childName">اسم المولود</Label>
            <Input
              id="childName"
              value={formData.childName}
              onChange={(e) => handleChange('childName', e.target.value)}
              placeholder="أدخل اسم المولود"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>الأب</Label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">{parentLineage || 'لم يتم التحديد'}</span>
            </div>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full hover:bg-secondary"
        disabled={!formData.requesterName || !formData.requesterPhone || !formData.childName || !formData.parentId}
      >
        إرسال الطلب
      </Button>
    </form>
  );
}